// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Stack, Box, Typography, Checkbox, FormControlLabel, Button } from '@metarouter-private/mr-mui';
import SearchInput from './SearchInput';
import FileRow from './FileRow';

export default function FileSelectionList({ files, selectedIds, onSelectionChange }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFiles = useMemo(() => {
    if (!searchQuery.trim()) {
      return files;
    }
    const query = searchQuery.toLowerCase();
    return files.filter(file => file.pipeline?.name?.toLowerCase().includes(query));
  }, [files, searchQuery]);

  const allFilteredSelected = filteredFiles.length > 0 && filteredFiles.every(file => selectedIds.has(file.id));

  const handleSelectAll = () => {
    const newSelectedIds = new Set(selectedIds);
    filteredFiles.forEach(file => newSelectedIds.add(file.id));
    onSelectionChange(newSelectedIds);
  };

  const handleDeselectAll = () => {
    const newSelectedIds = new Set(selectedIds);
    filteredFiles.forEach(file => newSelectedIds.delete(file.id));
    onSelectionChange(newSelectedIds);
  };

  const handleToggle = fileId => {
    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(fileId)) {
      newSelectedIds.delete(fileId);
    } else {
      newSelectedIds.add(fileId);
    }
    onSelectionChange(newSelectedIds);
  };

  return (
    <Stack spacing={2}>
      <SearchInput value={searchQuery} onChange={setSearchQuery} />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              checked={allFilteredSelected}
              indeterminate={selectedIds.size > 0 && !allFilteredSelected}
              onChange={allFilteredSelected ? handleDeselectAll : handleSelectAll}
            />
          }
          label="Select All"
        />
        <Button variant="text" size="small" onClick={handleDeselectAll} disabled={selectedIds.size === 0}>
          Deselect All
        </Button>
      </Stack>

      <Box
        sx={{
          maxHeight: 300,
          overflowY: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {filteredFiles.length === 0 ? (
          <Typography sx={{ p: 2 }} color="text.secondary">
            No files found
          </Typography>
        ) : (
          filteredFiles.map(file => (
            <FileRow
              key={file.id}
              file={file}
              isSelected={selectedIds.has(file.id)}
              onToggle={() => handleToggle(file.id)}
            />
          ))
        )}
      </Box>

      <Typography variant="body2" color="text.secondary">
        Selected: {selectedIds.size} of {files.length} files
      </Typography>
    </Stack>
  );
}

FileSelectionList.propTypes = {
  files: PropTypes.array.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
};
